import { type ReactNode, createContext, useEffect, useState } from 'react';

import { type Language, type TranslationKeys, ar, en } from './translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  direction: 'rtl' | 'ltr';
  translations: TranslationKeys;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

const STORAGE_KEY = 'preferred_language';

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'ar';

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'ar' || stored === 'en') {
    return stored;
  }

  return 'ar';
}

function getTranslations(language: Language): TranslationKeys {
  return language === 'ar' ? ar : en;
}

function getDirection(language: Language): 'rtl' | 'ltr' {
  return language === 'ar' ? 'rtl' : 'ltr';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  useEffect(() => {
    const direction = getDirection(language);
    const lang = language;

    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', lang);
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    direction: getDirection(language),
    translations: getTranslations(language),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
